import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiMail } from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/terms.scss";

const LAST_UPDATED = "August 12, 2026";

export function PolicySection({ id, title, children }) {
  return (
    <section className="policy-page__section" id={id}>
      <h2>{title}</h2>
      <div className="policy-page__section-body">{children}</div>
    </section>
  );
}

export function PolicyNotice({ children, tone = "default", title }) {
  return (
    <aside className={`policy-page__notice policy-page__notice--${tone}`}>
      {title && <strong>{title}</strong>}
      <div>{children}</div>
    </aside>
  );
}

export function PolicyLayout({
  children,
  eyebrow,
  intro,
  modifier,
  sections,
  title,
  updated = LAST_UPDATED,
}) {
  return (
    <main className={`policy-page policy-page--${modifier}`}>
      <header className="policy-page__hero">
        <div className="policy-page__shell">
          <Link className="policy-page__back" to="/shop">
            <FiArrowLeft aria-hidden="true" /> Back to shop
          </Link>
          <p className="policy-page__eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p className="policy-page__intro">{intro}</p>
          <div className="policy-page__meta">
            <span>Last updated</span>
            <strong>{updated}</strong>
          </div>
        </div>
      </header>

      <div className="policy-page__shell policy-page__layout">
        <aside className="policy-page__contents" aria-label={`${title} contents`}>
          <p>On this page</p>
          <ol>
            {sections.map((section, index) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {section.label}
                </a>
              </li>
            ))}
          </ol>
        </aside>

        <article className="policy-page__article">
          {children}
          <section className="policy-page__support" aria-labelledby={`${modifier}-support-title`}>
            <div>
              <p className="policy-page__eyebrow">Need a hand?</p>
              <h2 id={`${modifier}-support-title`}>We are happy to clarify.</h2>
              <p>
                Contact {siteConfig.brandName} before placing an order if any part
                of this policy is unclear.
              </p>
            </div>
            <div className="policy-page__support-actions">
              <a href={`mailto:${siteConfig.contact.email}`}>
                <FiMail aria-hidden="true" /> {siteConfig.contact.email}
              </a>
              <Link to="/contact">
                Contact customer care <FiArrowRight aria-hidden="true" />
              </Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

const termsSections = [
  { id: "agreement", label: "Agreement to these terms" },
  { id: "storefront-orders", label: "Storefront and orders" },
  { id: "product-information", label: "Product information" },
  { id: "customer-responsibilities", label: "Your responsibilities" },
  { id: "acceptable-use", label: "Acceptable use" },
  { id: "ownership", label: "Content and ownership" },
  { id: "availability-liability", label: "Availability and liability" },
  { id: "changes-contact", label: "Changes and contact" },
];

function Terms() {
  return (
    <PolicyLayout
      eyebrow="Legal · Terms"
      intro={`These terms explain the rules for using the ${siteConfig.brandName} storefront, including cart, checkout, payment, and delivery.`}
      modifier="terms"
      sections={termsSections}
      title="Terms of use"
    >
      <PolicyNotice title="Order acceptance">
        <p>
          Submitting checkout details redirects you to our payment endpoint. An
          order is accepted only after payment is confirmed and we issue confirmation.
        </p>
      </PolicyNotice>

      <PolicySection id="agreement" title="1. Agreement to these terms">
        <p>
          By accessing or using this website, you agree to these terms and to the
          policies linked from them. If you do not agree, please stop using the
          storefront. “You” means the person browsing or using the site; “we,”
          “us,” and “our” mean {siteConfig.brandName}.
        </p>
        <p>
          You must be old enough to enter a contract in your place of residence to
          place an order. A parent or guardian is responsible for a minor&apos;s permitted use.
        </p>
      </PolicySection>

      <PolicySection id="storefront-orders" title="2. Storefront and orders">
        <p>
          The cart and wishlist may retain state in your browser. Checkout sends
          the order number, payable amount, currency, and email to our payment endpoint.
        </p>
        <ul>
          <li>No inventory is reserved when an item is added to the cart.</li>
          <li>No estimated delivery date is a carrier commitment.</li>
        </ul>
        <p>
          The final price, taxes, shipping,
          availability, and order terms displayed at the time of payment will
          control. We may reject or cancel a live order for pricing errors,
          unavailable stock, suspected fraud, delivery restrictions, or quantity
          limits, with any captured payment returned as required.
        </p>
      </PolicySection>

      <PolicySection id="product-information" title="3. Product information">
        <p>
          Product names, descriptions, tasting notes, attributes, prices, and
          photography in this project are original content. We aim
          for clarity, but screen color, natural variation, seasonal substitutions,
          and packaging updates can affect how a real item appears.
        </p>
        <p>
          Food descriptions are not medical advice. Ingredient, allergen, origin,
          storage, and dietary information should be verified on the physical label
          before consumption. Contact us before ordering if an allergy or dietary
          restriction is involved; cross-contact may occur in production or packing
          facilities.
        </p>
      </PolicySection>

      <PolicySection id="customer-responsibilities" title="4. Your responsibilities">
        <p>When using the storefront, you agree to:</p>
        <ul>
          <li>provide accurate contact, billing, delivery, and recipient details;</li>
          <li>confirm that someone can promptly receive temperature-sensitive food;</li>
          <li>review your cart, quantities, and delivery selections before checkout;</li>
          <li>keep any account credentials private if accounts are later enabled; and</li>
          <li>use products safely and follow label storage instructions.</li>
        </ul>
        <p>
          You are responsible for errors in an address you provide and for ensuring
          a gift recipient is available to receive a perishable parcel.
        </p>
      </PolicySection>

      <PolicySection id="acceptable-use" title="5. Acceptable use">
        <p>You may not use the site to:</p>
        <ul>
          <li>break the law, infringe another person&apos;s rights, or misrepresent your identity;</li>
          <li>introduce malicious code or attempt to bypass site security;</li>
          <li>scrape, copy, or overload the service in a way that disrupts normal use;</li>
          <li>interfere with checkout, pricing, inventory, or another shopper&apos;s session; or</li>
          <li>resell site content or use our branding to imply an unauthorized relationship.</li>
        </ul>
        <p>
          We may limit or block access when reasonably necessary to protect the
          storefront, customers, or our rights.
        </p>
      </PolicySection>

      <PolicySection id="ownership" title="6. Content and ownership">
        <p>
          The storefront design, written content, product data, graphics, and
          original brand materials are owned by or licensed to {siteConfig.brandName}
          and are protected by applicable intellectual-property laws. You may view
          and use them for personal shopping purposes only.
        </p>
        <p>
          Product and company names belonging to others remain the property of
          their respective owners. Linking to the site is welcome when the link is
          accurate, lawful, and does not suggest endorsement.
        </p>
      </PolicySection>

      <PolicySection id="availability-liability" title="7. Availability and liability">
        <p>
          We work to keep the site accurate and available, but do not promise
          uninterrupted or error-free operation. The storefront is provided “as
          available” and may be changed, paused, or removed when necessary.
        </p>
        <p>
          To the fullest extent permitted by law, we are not responsible for
          indirect, incidental, or consequential loss arising from use of the site,
          reliance on product content, or links to third-party services.
          Nothing here excludes rights or remedies that cannot legally be excluded.
        </p>
      </PolicySection>

      <PolicySection id="changes-contact" title="8. Changes and contact">
        <p>
          We may update these terms when the storefront, applicable law, or our
          operating practices change. The date at the top shows the latest revision.
          Material changes take effect when the updated terms are posted.
        </p>
        <p>
          Questions can be sent to <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
          {` `}or mailed to {siteConfig.contact.address}. Customer-care hours are {siteConfig.contact.hours}.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export default Terms;
